/**
 * Availability — BL invoert wanneer medewerkers (niet) inzetbaar zijn.
 *
 * Drives the phase 1b pipeline gating: per-location switch `availability_enabled`
 * controls whether the AI generator reads these records. Owner/admin flips the
 * switch; BL is the primary data-entry point.
 *
 * Rules per contract type (from services/availability.is_oproep_like):
 *   • Vast (contract_hours > 0)  → default WEL inzetbaar; BL voert uitzonderingen in.
 *   • Oproep / 0-uren            → default NIET inzetbaar; BL voert opt-ins in.
 *
 * UI mirrors that asymmetry:
 *   • Green dot + "Vast 40u" badge — cell defaults to ✓; click → cycle to vrij/ziek.
 *   • Orange dot + "Oproep" badge — cell defaults to ·; click → ✓ (opt-in).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationUrlSync } from '@/contexts/LocationContext';
import api from '@/lib/api';
import { getISOWeek, isoWeekDates } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  AlertTriangle, CalendarDays, ChevronLeft, ChevronRight,
  Check, Loader2, Power, X,
} from 'lucide-react';

const DOW_LABELS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

// Mirror of services.availability.is_oproep_like (data-truth: 75% of prod
// employees stand as 'vast' but work 0-hours — hours is the authority).
function isOproepLike(emp) {
  if (!emp) return true;
  if (emp.contract_type === 'oproep') return true;
  if (emp.contract_hours == null) return true;
  const h = Number(emp.contract_hours);
  return Number.isNaN(h) ? true : h === 0;
}

function todayISOWeek() {
  return getISOWeek(new Date());
}

function shiftWeek(year, week, delta) {
  // Add 7*delta days to the monday of (year, week) and re-derive the ISO week.
  const dates = isoWeekDates(year, week);
  const monday = new Date(dates[0]);
  monday.setUTCDate(monday.getUTCDate() + 7 * delta);
  return getISOWeek(monday);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function Availability() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { slug: selectedSlug, setSlug: setSelectedSlug } = useLocationUrlSync();
  const canToggle = user?.role === 'owner' || user?.role === 'admin';

  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);  // raw availability rows
  const [{ year, week }, setYW] = useState(() => todayISOWeek());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(null); // 'on' | 'off' | null
  const [reasonPrompt, setReasonPrompt] = useState(null); // { empId, date, defaultReason }
  const [dirty, setDirty] = useState({});  // key 'empId|date' → desired state {available, reason}

  const weekDates = useMemo(
    () => isoWeekDates(year, week).map((d) => d.toISOString().slice(0, 10)),
    [year, week],
  );

  // ------- Load locations + selected employees + week's availability -------
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/locations');
        setLocations(data);
      } catch (_e) {
        toast({ title: 'Kon locaties niet laden', variant: 'destructive' });
      }
    })();
  }, [toast]);

  useEffect(() => {
    if (locations.length > 0 && !selectedSlug) {
      setSelectedSlug(locations[0].slug);
    }
  }, [locations, selectedSlug, setSelectedSlug]);

  const currentLocation = useMemo(
    () => locations.find((l) => l.slug === selectedSlug) || null,
    [locations, selectedSlug],
  );
  const flagOn = !!(currentLocation?.availability_enabled ?? false);

  const reload = useCallback(async () => {
    if (!selectedSlug) return;
    setLoading(true);
    setDirty({});
    try {
      const [empResp, avResp] = await Promise.all([
        api.get(`/employees?location_slug=${selectedSlug}&status=actief`),
        api.get(
          `/employee-availability?location_id=${selectedSlug}`
          + `&date_from=${weekDates[0]}&date_to=${weekDates[6]}`,
        ),
      ]);
      setEmployees(empResp.data || []);
      setEntries(avResp.data?.items || []);
    } catch (_e) {
      toast({ title: 'Kon beschikbaarheid niet laden', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedSlug, weekDates, toast]);

  useEffect(() => { reload(); }, [reload]);

  // ------- Per-cell state ----------------------------------------------------
  // Three sources, in priority order:
  //   1. dirty (unsaved local edit)
  //   2. existing availability row from server
  //   3. default per contract type
  function cellState(empId, date) {
    const k = `${empId}|${date}`;
    if (k in dirty) return dirty[k];                  // unsaved edit
    const row = entries.find((e) => e.employee_id === empId && e.date === date);
    if (row) return { available: !!row.available, reason: row.reason || '', _id: row.id };
    // No row — return default
    const emp = employees.find((e) => e.id === empId);
    if (isOproepLike(emp)) return { available: false, _default: true };
    return { available: true, _default: true };
  }

  function setCell(empId, date, next) {
    const k = `${empId}|${date}`;
    setDirty((prev) => ({ ...prev, [k]: next }));
  }

  // ------- Cell click handler ------------------------------------------------
  function onCellClick(empId, date) {
    const emp = employees.find((e) => e.id === empId);
    const oproep = isOproepLike(emp);
    const cur = cellState(empId, date);
    if (oproep) {
      // Oproep: cycle false (default) → true (opt-in) → false
      setCell(empId, date, { available: !cur.available, reason: cur.available ? '' : 'kan' });
    } else {
      // Vast: cycle true (default) → false+reason → true
      if (cur.available) {
        setReasonPrompt({ empId, date, defaultReason: 'vrij' });
      } else {
        setCell(empId, date, { available: true, reason: '' });
      }
    }
  }

  function applyReason(reasonText) {
    if (!reasonPrompt) return;
    const { empId, date } = reasonPrompt;
    setCell(empId, date, { available: false, reason: reasonText || 'vrij' });
    setReasonPrompt(null);
  }

  // ------- Bulk actions ------------------------------------------------------
  function bulkRowUnavailable(empId) {
    // 'hele week niet beschikbaar'
    const next = { ...dirty };
    weekDates.forEach((d) => {
      next[`${empId}|${d}`] = { available: false, reason: 'vrij' };
    });
    setDirty(next);
  }

  function bulkColUnavailable(date) {
    // 'iedereen niet inzetbaar deze dag'
    const next = { ...dirty };
    employees.forEach((emp) => {
      next[`${emp.id}|${date}`] = { available: false, reason: 'gesloten' };
    });
    setDirty(next);
  }

  // ------- Save --------------------------------------------------------------
  async function handleSave() {
    if (saving) return;
    const dirtyKeys = Object.keys(dirty);
    if (dirtyKeys.length === 0) {
      toast({ title: 'Niets om op te slaan' });
      return;
    }
    setSaving(true);
    try {
      // Split into upserts (POST /bulk) and deletes (if user reverts an override
      // back to its default state). For phase 2b: simple upsert-everything path.
      const payload = {
        entries: dirtyKeys.map((k) => {
          const [empId, date] = k.split('|');
          const v = dirty[k];
          return {
            employee_id: empId, date,
            available: !!v.available,
            reason: v.reason || null,
          };
        }),
      };
      await api.post('/employee-availability/bulk', payload);
      toast({ title: `${dirtyKeys.length} entries opgeslagen` });
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Onbekende fout';
      toast({ title: 'Opslaan mislukt', description: String(detail), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  // ------- Flag toggle (owner/admin) ----------------------------------------
  async function performToggle(next) {
    setConfirmOpen(null);
    if (!currentLocation) return;
    try {
      // Need fresh ETag from single-GET. Starlette's GzipMiddleware prepends
      // ``W/`` to the ETag on responses big enough to compress (location docs
      // hit that threshold; smaller docs on other pages don't, which is why
      // their PUTs work without this strip). Backend ``parse_if_match``
      // doesn't handle weak validators, so we hand it the strong form.
      const detail = await api.get(`/locations/${selectedSlug}`);
      const rawEtag = detail.headers.etag || '';
      const etag = rawEtag.replace(/^W\//, '');
      const r = await api.put(
        `/locations/${selectedSlug}`,
        { availability_enabled: next },
        { headers: { 'If-Match': etag } },
      );
      // Update local cache
      setLocations((prev) => prev.map(
        (l) => (l.slug === selectedSlug ? { ...l, availability_enabled: r.data.availability_enabled } : l),
      ));
      toast({ title: next ? 'Beschikbaarheid AAN' : 'Beschikbaarheid UIT' });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Onbekende fout';
      toast({
        title: 'Schakelen mislukt', description: String(detail), variant: 'destructive',
      });
    }
  }

  // ------- Mismatch banner (owner/admin only) -------------------------------
  const mismatchCount = useMemo(() => {
    if (!canToggle) return 0;
    return employees.filter(
      (e) => e.contract_type === 'vast' && Number(e.contract_hours || 0) === 0,
    ).length;
  }, [employees, canToggle]);

  // ------- Render ------------------------------------------------------------
  return (
    <div className="p-6 space-y-4" data-testid="availability-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-6 h-6 text-emerald-600" />
          <h1 className="text-2xl font-semibold">Beschikbaarheid</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => setYW(shiftWeek(year, week, -1))}
            data-testid="availability-week-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-3 py-1 rounded-md bg-slate-100 text-sm font-medium" data-testid="availability-week-label">
            Week {String(week).padStart(2, '0')} · {year}
          </span>
          <Button
            variant="outline" size="sm"
            onClick={() => setYW(shiftWeek(year, week, 1))}
            data-testid="availability-week-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Location selector */}
      <div className="flex items-center gap-3">
        <label htmlFor="loc" className="text-sm text-slate-600">Locatie:</label>
        <select
          id="loc"
          className="border rounded-md px-3 py-2 text-sm"
          value={selectedSlug || ''}
          onChange={(e) => setSelectedSlug(e.target.value)}
          data-testid="availability-location-select"
        >
          {locations.map((l) => (
            <option key={l.slug} value={l.slug}>{l.name}</option>
          ))}
        </select>
      </div>

      {/* Sticky flag banner */}
      {currentLocation && (
        <div
          className={`sticky top-0 z-10 rounded-lg border p-4 ${
            flagOn ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'
          }`}
          data-testid="availability-flag-banner"
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Power className={`w-5 h-5 ${flagOn ? 'text-emerald-700' : 'text-amber-700'}`} />
              <div>
                <div className="font-semibold" data-testid="availability-flag-status-badge">
                  Beschikbaarheidsmodel: {flagOn ? 'AAN' : 'UIT'}
                </div>
                <div className="text-sm text-slate-700">
                  {flagOn
                    ? 'Generator gebruikt deze beschikbaarheid + leave_records. Oproepers zonder opt-in worden NIET ingezet.'
                    : canToggle
                      ? 'Generator negeert de beschikbaarheid. Zet AAN als de BL klaar is.'
                      : 'Je invoer wordt opgeslagen, maar de generator gebruikt hem nog niet. Laat owner/admin de schakelaar AAN zetten als je klaar bent.'}
                </div>
              </div>
            </div>
            {canToggle && (
              <Button
                variant={flagOn ? 'destructive' : 'default'}
                size="sm"
                onClick={() => setConfirmOpen(flagOn ? 'off' : 'on')}
                data-testid="availability-flag-toggle-btn"
              >
                {flagOn ? 'Zet UIT' : 'Zet AAN'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Owner/admin mismatch banner */}
      {canToggle && mismatchCount > 0 && (
        <div
          className="rounded-lg border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900 flex items-start gap-3"
          data-testid="availability-mismatch-banner"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5" />
          <div>
            <strong>{mismatchCount} medewerker(s)</strong> staan geregistreerd als &apos;vast&apos; maar werken op 0-uren.
            Deze worden door de generator behandeld als <strong>oproep</strong> (standaard niet inzetbaar).
            Controleer evt. de contracttype-administratie in <Link to="/medewerkers" className="underline">Medewerkers</Link>.
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="text-slate-500 flex items-center gap-2 py-8 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Laden…
        </div>
      )}

      {/* Week grid */}
      {!loading && employees.length > 0 && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm" data-testid="availability-grid">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 w-64 sticky left-0 bg-slate-50 z-[1]">Medewerker</th>
                {weekDates.map((d, i) => (
                  <th key={d} className="p-2 text-center min-w-[80px]">
                    <div className="text-xs uppercase tracking-wider text-slate-500">
                      {DOW_LABELS[i]}
                    </div>
                    <div className="text-xs">{d.slice(5)}</div>
                    <button
                      type="button"
                      onClick={() => bulkColUnavailable(d)}
                      className="text-[10px] text-slate-400 hover:text-slate-700 mt-1"
                      title="Iedereen niet inzetbaar deze dag"
                      data-testid={`availability-bulk-col-${d}`}
                    >
                      [allen ✗]
                    </button>
                  </th>
                ))}
                <th className="p-2 text-center w-24">Bulk</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => {
                const oproep = isOproepLike(emp);
                return (
                  <tr key={emp.id} className="border-t hover:bg-slate-50/50">
                    <td className="p-3 sticky left-0 bg-white">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block w-2.5 h-2.5 rounded-full ${
                            oproep ? 'bg-orange-500' : 'bg-emerald-500'
                          }`}
                          aria-label={oproep ? 'oproep' : 'vast'}
                          data-testid={`availability-emp-dot-${emp.id}`}
                        />
                        <div>
                          <div className="font-medium">{emp.name}</div>
                          <Badge variant={oproep ? 'outline' : 'secondary'} className="text-xs mt-0.5">
                            {oproep
                              ? 'Oproep'
                              : `Vast ${Number(emp.contract_hours || 0)}u`}
                          </Badge>
                        </div>
                      </div>
                    </td>
                    {weekDates.map((d) => {
                      const cs = cellState(emp.id, d);
                      const isDirty = `${emp.id}|${d}` in dirty;
                      let icon = '·';
                      let cls = 'bg-slate-100 text-slate-400 hover:bg-slate-200';
                      if (cs.available) {
                        icon = '✓';
                        cls = oproep
                          ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                          : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200';
                      } else if (!oproep) {
                        icon = '✗';
                        cls = 'bg-rose-100 text-rose-800 hover:bg-rose-200';
                      }
                      return (
                        <td key={d} className="p-1 text-center">
                          <button
                            type="button"
                            onClick={() => onCellClick(emp.id, d)}
                            className={`w-full px-2 py-2 rounded font-medium text-sm transition-colors ${cls} ${
                              isDirty ? 'ring-2 ring-blue-400' : ''
                            }`}
                            title={cs.reason ? `${icon} — ${cs.reason}` : icon}
                            data-testid={`availability-cell-${emp.id}-${d}`}
                          >
                            <div className="flex flex-col items-center">
                              <span>{icon}</span>
                              {cs.reason && (
                                <span className="text-[10px] mt-0.5 opacity-80">{cs.reason}</span>
                              )}
                            </div>
                          </button>
                        </td>
                      );
                    })}
                    <td className="p-1 text-center">
                      <button
                        type="button"
                        onClick={() => bulkRowUnavailable(emp.id)}
                        className="text-[10px] text-slate-400 hover:text-rose-700"
                        title="Hele week niet inzetbaar"
                        data-testid={`availability-bulk-row-${emp.id}`}
                      >
                        [week ✗]
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state */}
      {!loading && employees.length === 0 && currentLocation && (
        <div className="text-center py-12 text-slate-500">
          Geen actieve medewerkers op deze locatie.
        </div>
      )}

      {/* Save bar */}
      {Object.keys(dirty).length > 0 && (
        <div className="sticky bottom-0 bg-white border-t shadow-md p-3 flex justify-between items-center">
          <span className="text-sm text-slate-600">
            {Object.keys(dirty).length} ongesaved wijziging(en)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline" onClick={() => setDirty({})}
              data-testid="availability-discard-btn"
            >
              Verwijder wijzigingen
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="availability-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Opslaan
            </Button>
          </div>
        </div>
      )}

      {/* Toggle confirm dialog */}
      {confirmOpen && (
        <ConfirmDialog
          variant={confirmOpen}
          onCancel={() => setConfirmOpen(null)}
          onConfirm={() => performToggle(confirmOpen === 'on')}
        />
      )}

      {/* Reason prompt dialog */}
      {reasonPrompt && (
        <ReasonDialog
          defaultReason={reasonPrompt.defaultReason}
          onCancel={() => setReasonPrompt(null)}
          onConfirm={applyReason}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function ConfirmDialog({ variant, onCancel, onConfirm }) {
  const isOn = variant === 'on';
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onCancel}
      data-testid="availability-confirm-dialog"
    >
      <div
        className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Power className={`w-5 h-5 ${isOn ? 'text-emerald-700' : 'text-amber-700'}`} />
          Beschikbaarheidsmodel {isOn ? 'AAN' : 'UIT'} zetten?
        </h3>
        <p className="text-sm text-slate-700 mb-4">
          {isOn
            ? 'Vanaf nu gebruikt de generator de beschikbaarheid voor deze locatie. Oproepers zonder opt-in worden NIET meer ingezet, en vaste medewerkers met een vrij/ziek-record worden overgeslagen.'
            : 'De generator negeert de beschikbaarheid weer. Het rooster wordt gegenereerd alsof iedereen beschikbaar is. Eerder ingevoerde availability-records blijven bewaard.'}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="availability-confirm-cancel-btn">
            Annuleren
          </Button>
          <Button
            variant={isOn ? 'default' : 'destructive'}
            onClick={onConfirm}
            data-testid="availability-confirm-ok-btn"
          >
            <Check className="w-4 h-4 mr-1" />
            {isOn ? 'Ja, zet AAN' : 'Ja, zet UIT'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReasonDialog({ defaultReason, onCancel, onConfirm }) {
  const [reason, setReason] = useState(defaultReason || '');
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onCancel}
      data-testid="availability-reason-dialog"
    >
      <div
        className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-3">Reden van afwezigheid</h3>
        <div className="space-y-2 mb-4">
          {['vrij', 'ziek', 'verlof', 'studie'].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(r)}
              className={`px-3 py-1.5 rounded-md border text-sm mr-2 ${
                reason === r ? 'bg-rose-100 border-rose-400' : 'bg-white hover:bg-slate-50'
              }`}
              data-testid={`availability-reason-preset-${r}`}
            >
              {r}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={200}
          className="w-full border rounded-md px-3 py-2 text-sm mb-4"
          placeholder="Of typ een eigen reden..."
          data-testid="availability-reason-input"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="availability-reason-cancel-btn">
            <X className="w-4 h-4 mr-1" /> Annuleren
          </Button>
          <Button onClick={() => onConfirm(reason)} data-testid="availability-reason-ok-btn">
            <Check className="w-4 h-4 mr-1" /> Opslaan
          </Button>
        </div>
      </div>
    </div>
  );
}
